import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link, useParams } from "react-router-dom";
import { Chargement, EnTetePage, Etiquette, Indicateur, Message, Vide } from "@cabinet/charte";
import { dossiersApi, ErreurApi, fournisseursApi, type Tranche } from "../api/client";
import { formaterDate, formaterMontant, formaterNombre } from "../domaine/formats";

/**
 * Échéancier fournisseurs et balance âgée (EF-0506).
 *
 * L'état ne se tient pas sur les factures mais sur les **écritures validées
 * non lettrées** : c'est la seule vue qui reste juste après un règlement
 * partiel, un avoir ou un écart de règlement. Une facture réglée en sort
 * parce qu'elle a été lettrée, non parce qu'un état l'a décidé.
 */
export function PageEcheancier() {
  const { id = "" } = useParams();
  const [au, setAu] = useState("");
  const [tiers, setTiers] = useState("");

  const dossier = useQuery({ queryKey: ["dossier", id], queryFn: () => dossiersApi.consulter(id) });
  const echeancier = useQuery({
    queryKey: ["echeancier", id, au, tiers],
    queryFn: () => fournisseursApi.echeancier(id, { au: au || undefined, tiers: tiers || undefined }),
  });

  const erreur = dossier.error ?? echeancier.error;
  const etat = echeancier.data;

  return (
    <>
      <EnTetePage
        surTitre={dossier.data?.raisonSociale ?? "Dossier"}
        titre="Échéancier fournisseurs"
        description="Les écritures validées qu'aucun lettrage ne solde, dans l'ordre où elles viennent à échéance, et leur balance âgée à la date d'arrêté. Un poste sans échéance vient en tête : il est à instruire."
        actions={
          etat != null ? (
            <Etiquette variante={etat.totalEchu > 0 ? "avertissement" : "succes"}>
              {etat.totalEchu > 0 ? `${formaterMontant(etat.totalEchu)} échus` : "Rien d'échu"}
            </Etiquette>
          ) : undefined
        }
      />

      <div className="filtres-plan">
        <input
          type="date"
          value={au}
          onChange={(e) => setAu(e.target.value)}
          aria-label="Arrêté au"
          title="Arrêté au"
        />
        <input
          value={tiers}
          onChange={(e) => setTiers(e.target.value.toUpperCase())}
          placeholder="Tiers — ATLAS"
          aria-label="Tiers"
        />
        {etat != null && (
          <span className="decompte">
            {formaterNombre(etat.postes.length)} postes · {etat.devise} · arrêté au{" "}
            {formaterDate(etat.dateDArrete)}
          </span>
        )}
      </div>

      {erreur != null && (
        <Message type="erreur" titre={erreur instanceof ErreurApi ? erreur.titre : "Erreur"}>
          <p style={{ margin: "6px 0 0" }}>{erreur.message}</p>
        </Message>
      )}

      {echeancier.isPending && <Chargement quoi="l'échéancier" />}

      {etat != null && etat.postes.length === 0 && (
        <Vide>
          Aucun poste ouvert : tout ce qui est validé sur les comptes de tiers est lettré, ou rien n'y est
          encore passé.
        </Vide>
      )}

      {etat != null && etat.postes.length > 0 && (
        <>
          <div className="carte">
            <div className="grille">
              <Indicateur libelle="Dû" valeur={formaterMontant(etat.total)} detail={etat.devise} />
              <Indicateur libelle="Échu" valeur={formaterMontant(etat.totalEchu)} detail="à régler" />
              <Indicateur libelle="À échoir" valeur={formaterMontant(etat.totalAEchoir)} detail="à venir" />
              {etat.postesSansEcheance > 0 && (
                <Indicateur
                  libelle="Sans échéance"
                  valeur={formaterNombre(etat.postesSansEcheance)}
                  detail="postes à dater"
                />
              )}
            </div>
          </div>

          <h3>Balance âgée</h3>
          <div className="tableau-conteneur">
            <table>
              <thead>
                <tr>
                  <th>Compte</th>
                  <th>Tiers</th>
                  <th className="nombre">Postes</th>
                  {etat.tranches.map((tranche) => (
                    <th key={tranche.code} className="nombre">
                      {tranche.libelle}
                    </th>
                  ))}
                  <th className="nombre">Total</th>
                </tr>
              </thead>
              <tbody>
                {etat.lignes.map((ligne) => (
                  <tr key={`${ligne.compte}-${ligne.tiers ?? ""}`}>
                    <td className="numero-compte">{ligne.compte}</td>
                    <td>
                      {ligne.tiers != null ? (
                        // Le lettrage est l'endroit où l'on solde ce que
                        // l'échéancier montre : l'y mener évite de retaper
                        // compte et tiers.
                        <Link to={`/dossiers/${id}/lettrage?compte=${ligne.compte}&tiers=${ligne.tiers}`}>
                          {ligne.tiers}
                        </Link>
                      ) : (
                        <span className="discret-texte">—</span>
                      )}
                    </td>
                    <td className="nombre">{formaterNombre(ligne.nombreDePostes)}</td>
                    {etat.tranches.map((tranche) => (
                      <td key={tranche.code} className="nombre">
                        {montantDeLaTranche(ligne.parTranche, tranche.code)}
                      </td>
                    ))}
                    <td className="nombre">{formaterMontant(ligne.total)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="total">
                  <td colSpan={3}>Totaux</td>
                  {etat.tranches.map((tranche) => (
                    <td key={tranche.code} className="nombre">
                      {formaterMontant(tranche.total)}
                    </td>
                  ))}
                  <td className="nombre">{formaterMontant(etat.total)}</td>
                </tr>
              </tfoot>
            </table>
          </div>

          <h3>Postes ouverts</h3>
          <div className="tableau-conteneur">
            <table>
              <thead>
                <tr>
                  <th>Échéance</th>
                  <th>Retard</th>
                  <th>Pièce</th>
                  <th>Date</th>
                  <th>Référence</th>
                  <th>Libellé</th>
                  <th>Tiers</th>
                  <th className="nombre">Restant dû</th>
                </tr>
              </thead>
              <tbody>
                {etat.postes.map((poste) => (
                  <tr key={`${poste.piece}:${poste.rang}`}>
                    <td>
                      {poste.echeance != null ? (
                        formaterDate(poste.echeance)
                      ) : (
                        <Etiquette variante="avertissement">Sans échéance</Etiquette>
                      )}
                    </td>
                    <td className={poste.joursDeRetard != null && poste.joursDeRetard > 0 ? "ecart" : "discret-texte"}>
                      {poste.joursDeRetard == null
                        ? "—"
                        : poste.joursDeRetard > 0
                          ? `${formaterNombre(poste.joursDeRetard)} j`
                          : "à échoir"}
                    </td>
                    <td className="discret-texte">
                      {poste.journal}
                      {poste.numero != null ? ` n° ${poste.numero}` : ""}
                    </td>
                    <td>{formaterDate(poste.date)}</td>
                    <td className="discret-texte">{poste.reference ?? "—"}</td>
                    <td>{poste.libelle}</td>
                    <td className="discret-texte">{poste.tiers ?? "—"}</td>
                    <td className="nombre">{formaterMontant(poste.restantDu)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </>
  );
}

/** Une case vide se lit mieux qu'un zéro dans une balance âgée : elle ne retient pas l'œil. */
function montantDeLaTranche(parTranche: Partial<Record<Tranche, number>>, tranche: Tranche): string {
  const montant = parTranche[tranche];
  return montant == null || montant === 0 ? "" : formaterMontant(montant);
}
